import { ChangeDetectorRef, Component, inject, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MatDividerModule } from '@angular/material/divider';
import { Router } from '@angular/router';
import {
  BroadcastService,
  ClientConstants,
  ConfigService,
  DataService,
  LoggedInUserService,
  NotificationService,
  OfflineStatusService,
  select,
  WebSocketService
} from '@formbird/services';
import { OfflineStatus, User } from '@formbird/types';
import { AuthenticationService } from '@services/authentication/authentication.service';
import { InitialisationService } from '@services/initialisation.service';
import { AboutDialogService } from '@services/modal/about-dialog.service';
import { ModalService } from '@services/modal/modal.service';
import { Observable, Subscription } from 'rxjs';

import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
const logger = console;

@Component({
  selector: 'ft-user',
  templateUrl: './user.component.html',
  styleUrls: ['./user.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [MatButtonModule, MatMenuModule, MatIconModule, MatBadgeModule, MatDividerModule, MatSlideToggleModule]
})
export class UserComponent implements OnInit, OnDestroy {
  private loggedInUserService = inject(LoggedInUserService);
  private configService = inject(ConfigService);
  private notificationService = inject(NotificationService);
  private dataService = inject(DataService);
  private initialisationService = inject(InitialisationService);
  private authenticationService = inject(AuthenticationService);
  private aboutDialogService = inject(AboutDialogService);
  private broadcastService = inject(BroadcastService);
  private router = inject(Router);
  private offlineStatusService = inject(OfflineStatusService);
  private cd = inject(ChangeDetectorRef);
  private modalService = inject(ModalService);
  private webSocketService = inject(WebSocketService);

  @select(['offlineReducer', 'offlineStatus']) offlineStatus$: Observable<OfflineStatus>;
  @select(['userState', 'user']) user$: Observable<User>;

  private subs: Subscription = new Subscription();

  user: any;
  connected: boolean;
  isCachingNotAllowed: boolean;
  cacheEnabled: boolean;
  operateOnline: boolean;

  enableCacheText = 'Enable';
  isFirstTime = true;
  deferredPrompt: any;
  offlineStatus: OfflineStatus;
  hideOperateOffline: boolean;
  backupInProgress = false;

  constructor() {
    // initialise the connected status to true to make it so that the online indicator will start as green and
    // change to red if disconnected
    this.connected = true;
    this.cacheEnabled = this.offlineStatusService.isCachingEnabled();
    const clientConfig = this.configService.clientConfig();
    this.isCachingNotAllowed = clientConfig?.cachingNotAllowed;
    this.hideOperateOffline = clientConfig?.offline?.hideOperateOffline;
    this.operateOnline = this.offlineStatusService.shouldOperateOnline();
  }


  ngOnInit() {
    const subUser = this.user$.subscribe((user: User) => {
      this.user = user;
      this.cd.detectChanges();
    });

    const subOfflineStatus = this.offlineStatusService.offlineStatus$.subscribe((offlineStatusData) => {
      this.connected = offlineStatusData.connected;
      this.cd.detectChanges();
      this.offlineStatus = offlineStatusData;
      this.operateOnline = this.offlineStatusService.shouldOperateOnline();
    });

    const subOfflineRedux = this.offlineStatus$.subscribe((offlineStatusData) => {
      this.connected = offlineStatusData.connected;
      this.cd.detectChanges();
      this.offlineStatus = offlineStatusData;
      this.operateOnline = this.offlineStatusService.shouldOperateOnline();
    });

    this.loggedInUserService.addUserConfigItemListener('cachingEnabled', newValue => {
      this.cacheEnabled = newValue;
    });

    const subInstall = this.broadcastService.on(ClientConstants.BEFORE_INSTALL_PROMPT).subscribe((deferredPrompt: any) => {
      this.deferredPrompt = deferredPrompt;
      this.cd.detectChanges();
    });

    this.subs.add(subUser);
    this.subs.add(subOfflineStatus);
    this.subs.add(subOfflineRedux);
    this.subs.add(subInstall);

  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  /**
  * @ngdoc method
  * @methodOf ft.core:ft-user
  * @name clearData
  * @description clears data
  * @function
  */
  async clearData() {
    if (!this.offlineStatusService.isConnected()) {
      this.notificationService.printMessage('Unable to clear the cache. Device is offline.', 'error');
    } else if (!this.cacheEnabled) {
      this.notificationService.printMessage('You did not enable caching.', 'error');
    } else {

      const succFunc = async () => {
        this.loggedInUserService.setUserConfigItem('isRunningCacheClear', true);
        this.modalService.showDialog('Clearing Cache. Please do not refresh or leave the page.', 'WARNING');
        const ok = await this.dataService.clearData();
        this.modalService.closeDialog();
        if (ok) {

          this.operateOnline = true;
          this.offlineStatusService.setOperateOnline(this.operateOnline);
          this.offlineStatusService.offlineStatus.errorCacheAttachedFile = [];
          this.offlineStatusService.offlineStatus.pendingDocumentCount = 0;
          this.loggedInUserService.setUserConfigItem('ErrorCachingAttachedFiles', this.offlineStatusService.offlineStatus.errorCacheAttachedFile);

          this.notificationService.printMessage('Your cache is now cleared.', 'success');
        } else {
          this.notificationService.printMessage('Error on deleting cache.', 'error');
        }
        await this.initialisationService.clearData();
      };

      const errFunc = () => {
        logger.info('Clearing cache is cancelled.');
      };

      let msg = 'Are you sure you wish to clear the cache?';
      if (this.offlineStatusService.offlineStatus.pendingDocumentCount > 0) {
        msg = 'Offline data has been saved and not sent to the server. Are you sure you wish to clear the cache?';
      }

      this.modalService.showDialog(msg, 'WARNING', succFunc, errFunc);
    }
  }

  async backupData() {
    if (this.backupInProgress) {
      return; // Prevent multiple clicks
    }

    if (this.cacheEnabled) {
      this.backupInProgress = true;
      this.cd.detectChanges();

            const CHUNK_SIZE =  this.configService.clientConfig().backupChunkSize || 1000;

            try {
                const fflate = await import('fflate');
                // file-saver is CommonJS-only (`module.exports = saveAs`), so the bundler
                // exposes it as the default export — a named `{ saveAs }` import resolves
                // to undefined at runtime.
                const { default: saveAs } = await import('file-saver');
                const zipFiles = {};
                let chunkCount = 0;
                await this.dataService.backupDataInChunks(CHUNK_SIZE, (chunk) => {
                    chunkCount++;
                    const stringData = JSON.stringify(chunk, null, 4);
                    zipFiles[`backup_chunk_${chunkCount}.json`] = fflate.strToU8(stringData);
                });

                if (chunkCount > 0) {
                    let zipped = fflate.zipSync(zipFiles); 
                    const safeBuffer = new Uint8Array(zipped); 
                    const blob = new Blob([safeBuffer], { type: 'application/zip' });
                    saveAs(blob, `backup_${new Date().getTime()}.zip`);
                    console.log(`Backup completed. Total chunks: ${chunkCount}`);
                    this.notificationService.printMessage('Backup completed successfully.', 'success');
                } else {
                    this.notificationService.printMessage('No data found to backup.', 'info');
                }
            } catch (error) {
                console.error('An error occurred during the backup process:', error);
                this.notificationService.printMessage('Backup failed. See logs for details.', 'error');
            } finally {
                this.backupInProgress = false;
                this.cd.detectChanges();
            }
        } else {
            this.notificationService.printMessage('You did not enable caching.', 'error');
        }
    }
  
  async enableCaching() {
    try {
      const msg = await this.initialisationService.enableCaching();
      this.notificationService.info(msg);
    } catch (error) {
      this.notificationService.error(error.message);
    }
  }

  showAboutDialog() {
    this.aboutDialogService.showAboutDialog();
  }

  openAccount() {
    if (this.user
      && this.user.account
      && this.user.account.documentId) {
      const paramQuery = this.user.account.documentId;
      logger.info('Navigating to logged in account document '
        + paramQuery);
      this.router.navigate(['/form', paramQuery]);
    } else {
      logger.warn('Account could not be found to navigate to');
      this.notificationService.error('Account could not be found to navigate to');
    }
  }

  async addToHomeScreen() {
    this.deferredPrompt.prompt();
    this.deferredPrompt.userChoice.then((choiceResult) => {
      if (choiceResult.outcome === 'accepted') {
        this.notificationService.printMessage('Added to Home Screen.', 'success');
      }
    });
  }

  logout() {
    this.authenticationService.logout();
  }

  logoutAllDevices() {
    this.authenticationService.logoutAllDevices();
  }

  /**
   * Get the CSS class for the status icon based on connection and caching state
   * @returns CSS class string for styling the status icon
   */
  getStatusIconClass(): string {
    if (this.cacheEnabled && !this.operateOnline) {
      return 'status-orange'; // Orange for offline/caching mode
    } else if (this.connected) {
      return 'status-success'; // Green for online
    } else {
      return 'status-warn'; // Red for offline/disconnected
    }
  }

}
